import { FormGroup, FormArray, FormControl, ValidatorFn } from '@angular/forms';
import { AdminEntity } from '@snelstart-ventures/fiscaal/domain-logic';

export type CustomerFormGroup = FormGroup<{
    id: FormControl<string | null>;
    code: FormControl<number | null>;
    isActive: FormControl<boolean | null>;
    name: FormControl<string | null>;
    contactName: FormControl<string | null>;
    address: AddressFormGroup;
    phoneNumber: FormControl<string | null>;
    mobileNumber: FormControl<string | null>;
    faxNumber: FormControl<string | null>;
    emailAddressLine: FormControl<string[] | null>;
    website: FormControl<string | null>;
    kvk: FormControl<string | null>;
    vat: FormControl<string | null>;
    oin: FormControl<string | null>;
    fiscalIdentifier: FormControl<string | null>;
}>;

export function createCustomerFormGroup(): CustomerFormGroup {
    return new FormGroup({
        id: new FormControl<string | null>(null),
        code: new FormControl<number | null>(null),
        isActive: new FormControl<boolean | null>(null),
        name: new FormControl<string | null>(null),
        contactName: new FormControl<string | null>(null),
        address: createAddressFormGroup(),
        phoneNumber: new FormControl<string | null>(null),
        mobileNumber: new FormControl<string | null>(null),
        faxNumber: new FormControl<string | null>(null),
        emailAddressLine: new FormControl<string[] | null>(null),
        website: new FormControl<string | null>(null),
        kvk: new FormControl<string | null>(null),
        vat: new FormControl<string | null>(null),
        oin: new FormControl<string | null>(null),
        fiscalIdentifier: new FormControl<string | null>(null),
    });
}

export type AddressFormGroup = FormGroup<{
    streetName: FormControl<string | null>;
    streetNumber: FormControl<number | null>;
    affix: FormControl<string | null>;
    postalCode: FormControl<string | null>;
    city: FormControl<string | null>;
    countryId: FormControl<string | null>;
}>;

export function createAddressFormGroup(): AddressFormGroup {
    return new FormGroup({
        streetName: new FormControl<string | null>(null),
        streetNumber: new FormControl<number | null>(null),
        affix: new FormControl<string | null>(null),
        postalCode: new FormControl<string | null>(null),
        city: new FormControl<string | null>(null),
        countryId: new FormControl<string | null>(null),
    });
}

export type AdministrationFormGroup = FormGroup<{
    id: FormControl<number | null>;
    name: FormControl<string | null>;
    administrationId: FormControl<string | null>;
    environmentId: FormControl<string | null>;
    providerId: FormControl<string | null>;
    rgs: FormControl<string | null>;
}>;

export function createAdministrationFormGroup(): AdministrationFormGroup {
    return new FormGroup({
        id: new FormControl<number | null>(null),
        name: new FormControl<string | null>(null),
        administrationId: new FormControl<string | null>(null),
        environmentId: new FormControl<string | null>(null),
        providerId: new FormControl<string | null>(null),
        rgs: new FormControl<string | null>(null),
    });
}

export function patchAdministrationFormGroup(formGroup: AdministrationFormGroup, administration: AdminEntity): void {
    formGroup.patchValue({
        id: administration.id,
        name: administration.name,
        administrationId: administration.administrationId,
        environmentId: administration.environmentId,
        providerId: administration.providerId,
        rgs: administration.rgs,
    });
}

export type AdministrationsFormGroup = FormGroup<{
    administrations: FormArray<AdministrationFormGroup>;
}>;

export function createAdministrationsFormGroup(): AdministrationsFormGroup {
    return new FormGroup({
        administrations: new FormArray<AdministrationFormGroup>([]),
    });
}

export type ClientFormGroup = FormGroup<{
    id: FormControl<number | null>;
    sstRelationId: FormControl<string | null>;
    activeMachtiging: FormControl<boolean>;
    deleted: FormControl<boolean>;
    taxId: FormControl<string | null>;
    taxIdVat: FormControl<string | null>;
    taxIdWageTax: FormControl<string | null>;
    adminEntities: FormArray<AdministrationFormGroup>;
    machtigingStatusToelichting: FormControl<string | null>;
    typeClient: FormControl<string | null>;
    rechtsvorm: FormControl<string | null>;
    aanspreekTitel: FormControl<string | null>;
    voorletters: FormControl<string | null>;
    tussenvoegsel: FormControl<string | null>;
    achternaam: FormControl<string | null>;
    roepnaam: FormControl<string | null>;
    name: FormControl<string | null>;
    postponement: FormControl<boolean>;
    incomeTax: FormControl<boolean>;
    valueAddedTax: FormControl<boolean>;
    corporateTax: FormControl<boolean>;
    wageTax: FormControl<boolean>;
    kvkId: FormControl<string | null>;
    classificatie: FormControl<string | null>;
    sbiCode: FormControl<string | null>;
    userIdGemachtigde: FormControl<number | null>;
    deadline: FormControl<Date | null>;
    street: FormControl<string | null>;
    houseNumber: FormControl<number | null>;
    numberAddition: FormControl<string | null>;
    postalCode: FormControl<string | null>;
    place: FormControl<string | null>;
    phone: FormControl<string | null>;
    mobile: FormControl<string | null>;
    email: FormControl<string | null>;
    website: FormControl<string | null>;
}>;

export function createClientFormGroup(): ClientFormGroup {
    return new FormGroup({
        id: new FormControl<number | null>(null),
        sstRelationId: new FormControl<string | null>(null),
        activeMachtiging: new FormControl<boolean>(false, { nonNullable: true }),
        deleted: new FormControl<boolean>(false, { nonNullable: true }),
        taxId: new FormControl<string | null>(null),
        taxIdVat: new FormControl<string | null>(null, omzetbelastingnummerValidator),
        taxIdWageTax: new FormControl<string | null>(null, loonheffingennummerValidator),
        adminEntities: new FormArray<AdministrationFormGroup>([]),
        machtigingStatusToelichting: new FormControl<string | null>(null),
        typeClient: new FormControl<string | null>(null),
        rechtsvorm: new FormControl<string | null>(null),
        aanspreekTitel: new FormControl<string | null>(null),
        voorletters: new FormControl<string | null>(null),
        tussenvoegsel: new FormControl<string | null>(null),
        achternaam: new FormControl<string | null>(null),
        roepnaam: new FormControl<string | null>(null),
        name: new FormControl<string | null>(null),
        postponement: new FormControl<boolean>(false, { nonNullable: true }),
        incomeTax: new FormControl<boolean>(false, { nonNullable: true }),
        valueAddedTax: new FormControl<boolean>(false, { nonNullable: true }),
        corporateTax: new FormControl<boolean>(false, { nonNullable: true }),
        wageTax: new FormControl<boolean>(false, { nonNullable: true }),
        kvkId: new FormControl<string | null>(null),
        classificatie: new FormControl<string | null>(null),
        sbiCode: new FormControl<string | null>(null),
        userIdGemachtigde: new FormControl<number | null>(null),
        deadline: new FormControl<Date | null>(null),
        street: new FormControl<string | null>(null),
        houseNumber: new FormControl<number | null>(null),
        numberAddition: new FormControl<string | null>(null),
        postalCode: new FormControl<string | null>(null),
        place: new FormControl<string | null>(null),
        phone: new FormControl<string | null>(null),
        mobile: new FormControl<string | null>(null),
        email: new FormControl<string | null>(null),
        website: new FormControl<string | null>(null),
    });
}

const ELFPROEF_WEIGHTS = [9, 8, 7, 6, 5, 4, 3, 2, -1];

function passesElfproef(fiscaalNummer: string): boolean {
    const digits = fiscaalNummer.split('').map(Number);

    // first three not all zero
    if (digits[0] === 0 && digits[1] === 0 && digits[2] === 0) {
        return false;
    }

    return digits.reduce((sum, digit, index) => sum + digit * ELFPROEF_WEIGHTS[index], 0) % 11 === 0;
}

const LOONHEFFINGEN_PATTERN = /^\d{9}L(0[1-9]|[1-9]\d)$/;

export const loonheffingennummerValidator: ValidatorFn = (control) => {
    const value: string = control.value;

    if (value == null || value === '') {
        return null;
    }

    if (!LOONHEFFINGEN_PATTERN.test(value)) {
        return { loonheffingenFormat: 'Ongeldig loonheffingennummer' };
    }

    return passesElfproef(value.slice(0, 9))
        ? null
        : { loonheffingenElfproef: 'Ongeldig loonheffingennummer' };
};

const OMZETBELASTING_PATTERN = /^\d{9}B(0[1-9]|[1-9]\d)$/;

export const omzetbelastingnummerValidator: ValidatorFn = (control) => {
    const value: string = control.value;

    if (value == null || value === '') {
        return null;
    }

    if (!OMZETBELASTING_PATTERN.test(value)) {
        return { omzetbelastingFormat: 'Ongeldig omzetbelastingnummer' };
    }

    return passesElfproef(value.slice(0, 9))
        ? null
        : { omzetbelastingElfproef: 'Ongeldig omzetbelastingnummer' };
};
